// @flow

import { observable, computed } from 'mobx';
import * as mobx from 'mobx';
import moment from 'moment';
import BsvSuffix from './BsvSuffix';
import EntnahmeGefaess from './EntnahmeGefaess';
import Methode from './Methode';
import Lagerung from './Lagerung';
import Bestimmung from './Bestimmung';
import AnalysenTyp from './AnalysenTyp';
import Sop from './Sop';
import Referenzwert from './Referenzwert';
import Bildhinweis from './Bildhinweis';
import Versand from './Versand';
import CrudModel from './CrudModel';
import HttpBackend from '../Services/HttpBackend';
import PageResult from './PageResult';
import AnalyseLabor from './AnalyseLabor';
import AnalyseMaterial from './AnalyseMaterial';
import ExterneQk from './ExterneQk';
import Probenstabilitaet from './Probenstabilitaet';
import AnalyseVersand from './AnalyseVersand';
import Primaerreferenz from './Primaerreferenz';
import LaborUebermittlung from "./LaborUebermittlung";
import LaborFormular from "./LaborFormular";

export default class Analyse extends CrudModel {
  @observable id: string;
  @observable akkreditiert_bemerkung: string;
  @observable akkreditiert_seit: string = moment().format('YYYY-MM-DD HH:mm:ss');
  @observable created_at: string;
  @observable updated_at: string;
  @observable analysentyp: AnalysenTyp = new AnalysenTyp();
  @observable bemerkungen: string;
  @observable bestimmung: Bestimmung = new Bestimmung();
  @observable bildhinweise: Array<Bildhinweis> = [];
  @observable bsv_code: string;
  @observable bsv_suffix: BsvSuffix = new BsvSuffix();
  @observable entnahmegefaess: EntnahmeGefaess = new EntnahmeGefaess();
  @observable eqk_anzahl: number;
  @observable externe_links: Array<any> = [];
  @observable externe_qk: ExterneQk = new ExterneQk();
  @observable frequenz: string;
  @observable hygienemassnahmen: string;
  @observable primaerreferenz: Primaerreferenz = new Primaerreferenz();
  @observable interne_qk: number;
  @observable labor: Array<AnalyseLabor> = [];
  @observable lagerung: Lagerung = new Lagerung();
  @observable literatur: string;
  @observable material: Array<AnalyseMaterial> = []; // TODO link in form
  @observable meldepflicht: string;
  @observable messungenauigkeit: string;
  @observable methode: Methode = new Methode();
  @observable name: string;
  @observable notfallanalyse: string;
  @observable notizen: string;
  @observable postanalytik: string;
  @observable praeanalytik: string;
  @observable probenstabilitaet: Probenstabilitaet = new Probenstabilitaet();
  @observable referenzwert: Referenzwert = new Referenzwert();
  @observable sop: Sop = new Sop();
  @observable tags: Array<any> = [];
  @observable taxpunkte: string;
  @observable versand: Array<AnalyseVersand> = [];
  @observable versand_hinweis: string;
  @observable visible: boolean = false;
  @observable visum: string;
  @observable uebermittlung: LaborUebermittlung = new LaborUebermittlung();
  @observable formular: LaborFormular = new LaborFormular();

  constructor(json?: any) {
    super('analyse', Analyse);
    if (json) {
      Object.assign(this, json);
      this.visible = !!this.visible;
      this.akkreditiert_seit = this.akkreditiert_seit || moment().format('YYYY-MM-DD HH:mm:ss');
      this.bsv_suffix = new BsvSuffix(json.bsv_suffix);
      this.entnahmegefaess = new EntnahmeGefaess(json.entnahmegefaess);
      this.methode = new Methode(json.methode);
      this.lagerung = new Lagerung(json.lagerung);
      this.bestimmung = new Bestimmung(json.bestimmung);
      this.analysentyp = new AnalysenTyp(json.analysentyp);
      this.externe_qk = new ExterneQk(json.externe_qk);
      this.sop = new Sop(json.sop);
      this.referenzwert = new Referenzwert(json.referenzwert);
      this.labor = (json.labor || []).map(l => new AnalyseLabor(l));
      this.material = (json.material || []).map(m => new AnalyseMaterial(m));
      this.probenstabilitaet = new Probenstabilitaet(json.probenstabilitaet);
      this.tags = json.tags || [];
      this.externe_links = json.externe_links || [];
      this.bildhinweise = (json.bildhinweise || []).map(b => new Bildhinweis(b));
      this.versand = (json.versand || []).map(v => new AnalyseVersand(v));
      this.uebermittlung = new LaborUebermittlung(json.uebermittlung);
      this.formular = new LaborFormular(json.formular);
    }
  }

  @computed
  get akkreditiert_seit_datum(): any {
    return moment(this.akkreditiert_seit);
  }

  @computed
  get aktualisierung_datum(): any {
    return moment(this.updated_at);
  }

  @computed
  get isNew(): boolean {
    return !this.id;
  }
  
  @computed
  get hasLabor(): boolean {
    return this.labor && this.labor.length > 0;
  }
  
  @computed
  get hasVersand(): boolean {
    return this.versand && this.versand.length > 0;
  }
  
  @computed
  get hasMaterial(): boolean {
    return this.material && this.material.length > 0;
  }
  
  @computed
  get hasReferenzwert(): boolean {
    if (this.referenzwert) {
      const { maennlich, erwachsen, weiblich, kind, neugeborenes } = this.referenzwert;
      return !!(maennlich || erwachsen || weiblich || kind || neugeborenes);
    }
    return false;
  }
  
  static async get(id: string): Promise<Analyse> {
    const res = await HttpBackend.get(`/api/analyse/internal/${id}`);
    if (res.ok) {
      return new Analyse(res.data);
    }
    throw new Error(res.error);
  }
  
  static async getPublic(id: string): Promise<Analyse> {
    const res = await HttpBackend.get(`/api/analyse/public/${id}`);
    if (res.ok) {
      return new Analyse(res.data);
    }
    throw new Error(res.error);
  }
  
  static async list(params?: any = {}): Promise<PageResult<Analyse>> {
    const res = await HttpBackend.get(`/api/analyse/internal`, params);
    if (res.ok) {
      res.data.data = res.data.data.map(j => new Analyse(j));
      return new PageResult(res.data);
    }
    throw new Error(res.error);
  }
  
  static async listPublic(params?: any = {}): Promise<PageResult<Analyse>> {
    const res = await HttpBackend.get(`/api/analyse/public`, params);
    if (res.ok) {
      res.data.data = res.data.data.map(j => new Analyse(j));
      return new PageResult(res.data);
    }
    throw new Error(res.error);
  }

  static async find(params?: any = {}): Promise<PageResult<Analyse>> {
    return await this.list(params);
  }
}



// WEBPACK FOOTER //
// ./src/App/Model/Analyse.js